import React, { useState } from "react";
import { Send, CheckCircle, Loader } from "lucide-react";

const LEAD_API_URL = "/api/leads";

const INITIAL = {
  first_name: "",
  last_name: "",
  email: "",
  company: "",
  salesforce_edition: "",
  biggest_challenge: "",
  phone: "",
};

const LeadCaptureSection = () => {
  const [form, setForm] = useState(INITIAL);
  const [status, setStatus] = useState("idle"); // idle | loading | success | error

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    try {
      const res = await fetch(LEAD_API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Submission failed");
      setStatus("success");
      setForm(INITIAL);
    } catch {
      setStatus("error");
    }
  };

  return (
    <section className="py-20 lg:py-28 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" />
      <div className="absolute top-1/4 left-1/4 w-72 h-72 bg-orange-500/10 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 right-1/4 w-72 h-72 bg-pink-500/10 rounded-full blur-3xl" />

      <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Heading */}
        <div className="text-center mb-12">
          <span className="inline-block bg-gradient-to-r from-orange-500/20 to-pink-500/20 border border-orange-500/30 text-orange-400 text-xs font-semibold px-4 py-1.5 rounded-full mb-4 uppercase tracking-wide">
            Get Early Access
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Ready to automate your{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-pink-400">
              document workflow?
            </span>
          </h2>
          <p className="text-slate-400 text-lg max-w-xl mx-auto">
            Leave your details and our team will reach out to set up a personalised demo.
          </p>
        </div>

        {/* Form card */}
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 shadow-2xl">
          {status === "success" ? (
            <div className="flex flex-col items-center justify-center py-12 gap-4 text-center">
              <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center">
                <CheckCircle className="text-green-400" size={32} />
              </div>
              <h3 className="text-xl font-bold text-white">Thanks! We'll be in touch soon.</h3>
              <p className="text-slate-400 text-sm">Our team typically responds within 1 business day.</p>
              <button
                onClick={() => setStatus("idle")}
                className="mt-2 text-orange-400 text-sm hover:text-orange-300 underline underline-offset-4"
              >
                Submit another enquiry
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Row 1: First + Last Name */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {[
                  { name: "first_name", label: "First Name", type: "text",  placeholder: "John",            required: true },
                  { name: "last_name",  label: "Last Name",  type: "text",  placeholder: "Smith",           required: true },
                  { name: "email",      label: "Work Email", type: "email", placeholder: "john@company.com", required: true },
                  { name: "company",    label: "Company",    type: "text",  placeholder: "Acme Corp",        required: true },
                ].map((field) => (
                  <div key={field.name}>
                    <label className="block text-sm font-medium text-slate-300 mb-1.5">
                      {field.label} <span className="text-orange-400">*</span>
                    </label>
                    <input
                      type={field.type}
                      name={field.name}
                      value={form[field.name]}
                      onChange={handleChange}
                      placeholder={field.placeholder}
                      required
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 transition-all"
                    />
                  </div>
                ))}
              </div>

              {/* Salesforce Edition — required */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1.5">
                  Salesforce Edition <span className="text-orange-400">*</span>
                </label>
                <select
                  name="salesforce_edition"
                  value={form.salesforce_edition}
                  onChange={handleChange}
                  required
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 transition-all appearance-none"
                  style={{ colorScheme: "dark" }}
                >
                  <option value="" disabled>Select your edition</option>
                  <option value="Essentials">Essentials</option>
                  <option value="Professional">Professional</option>
                  <option value="Enterprise">Enterprise</option>
                  <option value="Unlimited">Unlimited</option>
                  <option value="Developer">Developer</option>
                  <option value="Not using Salesforce yet">Not using Salesforce yet</option>
                </select>
              </div>

              {/* Row 3: Use Case + Phone */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1.5">
                    Biggest Challenge
                  </label>
                  <select
                    name="biggest_challenge"
                    value={form.biggest_challenge}
                    onChange={handleChange}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 transition-all appearance-none"
                    style={{ colorScheme: "dark" }}
                  >
                    <option value="">Select your challenge</option>
                    <option value="Manual document creation">Creating documents manually is time-consuming</option>
                    <option value="Copy-paste errors">Too many errors from copy-pasting Salesforce data</option>
                    <option value="No PDF from Salesforce">Can't generate PDFs directly from Salesforce</option>
                    <option value="Inconsistent branding">Documents lack consistent formatting & branding</option>
                    <option value="Slow approval cycles">Slow document approval & sending cycles</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1.5">
                    Phone <span className="text-orange-400">*</span>
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    placeholder="+1 (555) 000-0000"
                    required
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 transition-all"
                  />
                </div>
              </div>

              {status === "error" && (
                <p className="text-red-400 text-sm text-center">Something went wrong. Please try again.</p>
              )}

              <button
                type="submit"
                disabled={status === "loading"}
                className="w-full bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold py-3 px-6 rounded-lg transition-all duration-300 hover:scale-[1.02] hover:shadow-lg flex items-center justify-center gap-2"
              >
                {status === "loading" ? (
                  <>
                    <Loader className="animate-spin" size={18} />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send size={18} />
                    Send Enquiry
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

export default LeadCaptureSection;
